import { useEffect, useRef, lazy, Suspense } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTabSystem } from '../context/TabContext';
import { useAuth } from '../context/AuthContext';
import { SekmeAktifContext } from '../utils/useYoklama';

const PAGE_MAP = {
  '/panel/anasayfa': lazy(() => import('../pages/AnaSayfa')),
  '/panel/urunler': lazy(() => import('../pages/PanelUrunler')),
  '/panel/yeni-siparis': lazy(() => import('../pages/YeniSiparis')),
  '/panel/siparisler': lazy(() => import('../pages/Siparisler')),
  '/panel/siparis-izni-talepleri': lazy(() => import('../pages/SiparisIzniTalepleri')),
  '/panel/teklifler': lazy(() => import('../pages/TeklifYonetimi')),
  '/panel/teklif-listesi': lazy(() => import('../pages/TeklifListesi')),
  '/panel/hizli-teklif-olustur': lazy(() => import('../pages/HizliTeklifOlustur')),
  '/panel/cari': lazy(() => import('../pages/CariHesap')),
  '/panel/cari-hesaplar': lazy(() => import('../pages/CariHesaplarYeni')),
  '/panel/musteri-cari': lazy(() => import('../pages/MusteriCariHesap')),
  '/panel/musteriler': lazy(() => import('../pages/MusteriDashboard')),
  '/panel/panel-musterileri': lazy(() => import('../pages/PanelMusterileri')),
  '/panel/ithalat': lazy(() => import('../pages/Ithalat')),
  '/panel/depo-transfer': lazy(() => import('../pages/DepoTransfer')),
  '/panel/stok-resync': lazy(() => import('../pages/StokResync')),
  '/panel/yoldaki-urunlerim': lazy(() => import('../pages/YoldakiUrunlerim')),
  '/panel/on-siparislerim': lazy(() => import('../pages/OnSiparislerim')),
  '/panel/sepetim': lazy(() => import('../pages/Sepetim')),
  '/panel/raporlar': lazy(() => import('../pages/Raporlar')),
  '/panel/talep-radari': lazy(() => import('../pages/TalepRadari')),
  '/panel/planlama': lazy(() => import('../pages/Planlama')),
  '/panel/kasa': lazy(() => import('../pages/Kasa')),
  '/panel/tecdoc': lazy(() => import('../pages/TecDocArama')),
  '/panel/fiyat-talepleri': lazy(() => import('../pages/FiyatTalepleri')),
  '/panel/taleplerim': lazy(() => import('../pages/Taleplerim')),
  '/panel/iskonto-onay': lazy(() => import('../pages/IskontoOnay')),
  '/panel/iskonto-tanimlari': lazy(() => import('../pages/IskontoTanimlari')),
  '/panel/yeni-urun': lazy(() => import('../pages/YeniUrun')),
  '/panel/promosyon-merkezi': lazy(() => import('../pages/KampanyaDuyurular')),
  '/panel/kullanici-yonetimi': lazy(() => import('../pages/KullaniciYonetimi')),
  '/panel/fiyat-yonetimi': lazy(() => import('../pages/FiyatYonetimi')),
  '/panel/sistem-yonetimi': lazy(() => import('../pages/SistemYonetimi')),
  '/panel/firma-dashboard': lazy(() => import('../pages/FirmaDashboard')),
  '/panel/kredi-karti-provizyon': lazy(() => import('../pages/KrediKartiProvizyon')),
  '/panel/iade': lazy(() => import('../pages/IadeYonetimi')),
  '/panel/bayi-basvurulari': lazy(() => import('../pages/BayiBasvurulari')),
  '/panel/sistem-bildirimleri': lazy(() => import('../pages/SistemBildirimleri')),
  '/panel/uyelik': lazy(() => import('../pages/UyelikBilgilerim')),
  '/panel/adreslerim': lazy(() => import('../pages/Adreslerim')),
  '/panel/sifre-degistir': lazy(() => import('../pages/SifreDegistir')),
  '/panel/siparislerim': lazy(() => import('../pages/Siparislerim')),
  '/yonetici-dashboard': lazy(() => import('../pages/YoneticiDashboard')),
};

// CIP alt rotaları — decouple (cache'lenmez, her girişte fresh mount)
// '/panel/cip', '/panel/customer-intelligence', '/panel/musteri-gecmisi',
// '/panel/cohort-analysis', '/panel/kullanici-aktiviteleri', '/panel/canli-analitik'
// intentionally PAGE_MAP dışında tutulmuştur.

const TabLoading = () => (
  <div className="flex items-center justify-center h-64">
    <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

const FIRMA_DASHBOARD_ROLES = ['admin', 'superadmin', 'firma_kullanicisi', 'firma', 'mudur', 'yonetici'];

// 🔧 CIP alt sistemi tab sisteminden muaftır — tam ekran olarak render edilir.
// CRM gibi ayrı bir iş akışı olduğu için tab bar'a düşmez.
const CIP_DECOUPLED_PATHS = new Set([
  '/panel/cip',
  '/panel/customer-intelligence',
  '/panel/musteri-gecmisi',
  '/panel/cohort-analysis',
  '/panel/kullanici-aktiviteleri',
  '/panel/canli-analitik',
]);

const TabLayout = ({ children }) => {
  const { tabs, activeTabId, openTab, isTabEnabled, applyFullscreenPreference, loadPinnedForUser, pinnedLoaded, refreshTabByPath } = useTabSystem();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastPathRef = useRef('');
  const pinnedInitRef = useRef(false);

  const userRole = user?.role || '';
  const userId = user?.email || user?._id || '';
  const tabEnabled = isTabEnabled(userRole);

  // Login sonrası sabitlenmiş tab'ları yükle (bir kez)
  useEffect(() => {
    if (!tabEnabled || !userId || pinnedInitRef.current) return;
    pinnedInitRef.current = true;
    loadPinnedForUser(userId);
    applyFullscreenPreference();
  }, [tabEnabled, userId, loadPinnedForUser, applyFullscreenPreference]);

  // URL değiştiğinde tab aç
  useEffect(() => {
    if (!tabEnabled) return;
    let path = location.pathname;
    if (path === lastPathRef.current) return;

    // Firma/admin/mudur/superadmin → anasayfa yerine firma-dashboard aç
    if (path === '/panel/anasayfa' && FIRMA_DASHBOARD_ROLES.includes(userRole)) {
      lastPathRef.current = '/panel/firma-dashboard';
      openTab('/panel/firma-dashboard');
      navigate('/panel/firma-dashboard', { replace: true });
      return;
    }

    lastPathRef.current = path;
    // 🔧 CRM ve CIP ayrı bir sistem — tab bar'a dahil edilmez.
    if (CIP_DECOUPLED_PATHS.has(path) || path.startsWith('/panel/cip/') || path.startsWith('/panel/musteri-gecmisi/')) {
      return;
    }
    if (path.startsWith('/panel/') || path.startsWith('/wms/') || path === '/yonetici-dashboard') {
      openTab(path);
    }
  }, [location.pathname, tabEnabled, openTab, userRole, navigate]);

  // 🔧 Browser Back/Forward ile gelindiğinde aktif tab'ı remount et
  // Aksi halde cached sayfa stale state ile kalıyordu (örn. Fiyat Yönetimi'nden geri → eski veri görünüyor).
  useEffect(() => {
    if (!tabEnabled) return;
    const onPopState = () => {
      // Mikro-gecikme: react-router location update'ini bekle
      setTimeout(() => {
        try {
          refreshTabByPath(window.location.pathname);
        } catch (_) { /* ignore */ }
      }, 0);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [tabEnabled, refreshTabByPath]);

  // 🔧 CIP/CRM rotalarında tab render edilmez; komponent direkt (children) render edilir.
  const isDecoupled = CIP_DECOUPLED_PATHS.has(location.pathname)
    || location.pathname.startsWith('/panel/cip/')
    || location.pathname.startsWith('/panel/musteri-gecmisi/');
  if (!tabEnabled || isDecoupled) {
    return <>{children}</>;
  }

  const activeTab = tabs.find(t => t.id === activeTabId);
  const activeIsMapped = activeTab && PAGE_MAP[activeTab.path];

  return (
    <>
      {/* Mapped tab'lar - display:none/block ile STATE KORUNUR
          🔧 'contents' yerine 'block' kullanılıyor; 'contents' browserlar arası
          form/focus/modal stacking context'i bozuyor (Hızlı Teklif gibi karmaşık
          sayfalar tablar arası geçişte rastgele kırılıyordu). */}
      {tabs.map((tab) => {
        const PageComponent = PAGE_MAP[tab.path];
        if (!PageComponent) return null;
        const isActive = tab.id === activeTabId;
        return (
          <div
            key={`${tab.id}-${tab.refreshKey || 0}`}
            style={{ display: isActive ? 'block' : 'none' }}
          >
            {/* Arkadaki iç sekme yoklamasını durdurur (utils/useYoklama) — sayfa bağlı kalır, state korunur */}
            <SekmeAktifContext.Provider value={isActive}>
              <Suspense fallback={<TabLoading />}>
                <PageComponent />
              </Suspense>
            </SekmeAktifContext.Provider>
          </div>
        );
      })}
      {/* WMS / bilinmeyen path'ler: normal children (CRM tab sistemi dışında) */}
      {(!activeIsMapped) && children}
    </>
  );
};

export default TabLayout;
